import React, { useState } from "react";
import { motion } from "framer-motion";
import { PublicLayout } from "../components/Layout";
import { PRODUCT_CATEGORIES } from "../data/products";

const ProductCard = ({ product, accent, index }) => (
  <motion.div
    data-testid={`product-card-${product.id}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.55, delay: (index % 6) * 0.04, ease: [0.22, 1, 0.36, 1] }}
    className="group relative rounded-2xl overflow-hidden bg-white border border-neutral-200/70 lift-card"
  >
    <div className="aspect-[4/3] overflow-hidden bg-white relative">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className={`w-full h-full ${product.fit === "contain" ? "object-contain p-3" : "object-cover"} transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#222629]/85 via-[#222629]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      {product.desc && (
        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <p className="text-xs md:text-sm text-white leading-relaxed">{product.desc}</p>
        </div>
      )}
    </div>
    <div className="p-4 md:p-5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.28em] uppercase mb-1" style={{ color: accent }}>Item</div>
        <h4 className="font-heading text-[15px] md:text-base font-semibold text-[#222629] leading-tight truncate">{product.name}</h4>
      </div>
    </div>
  </motion.div>
);

const Products = () => {
  const [active, setActive] = useState(PRODUCT_CATEGORIES[0].id);
  const activeCat = PRODUCT_CATEGORIES.find(c => c.id === active);

  return (
    <PublicLayout>
      <section className="pt-28 md:pt-32 pb-12 md:pb-16 bg-[#F7F8F5] relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">Product Portfolio</div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight text-[#222629] max-w-4xl leading-[1.02]">
            A material for every industry.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-600 max-w-2xl leading-relaxed">
            Responsibly procured, meticulously sorted and sustainably processed — supporting a cleaner circular industrial economy.
          </p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="sticky top-16 md:top-20 z-30 bg-[#F7F8F5]/95 backdrop-blur-xl border-b border-neutral-200">
        <div className="no-scrollbar snap-scroll flex gap-3 overflow-x-auto py-4 px-5 sm:px-6 md:px-10 max-w-7xl mx-auto">
          {PRODUCT_CATEGORIES.map(c => (
            <button
              key={c.id}
              data-testid={`category-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`btn-pill text-sm whitespace-nowrap shrink-0 ${active === c.id ? "btn-primary" : "btn-ghost bg-white"}`}
            >
              {c.name}
            </button>
          ))}
          <div className="shrink-0 w-2" aria-hidden />
        </div>
      </section>

      {/* Intro + subcategories */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-10">
          <motion.div
            key={activeCat.id + "-intro"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: activeCat.accent }}>{activeCat.name}</div>
            <p className="text-lg md:text-2xl text-[#222629] font-heading leading-[1.35] max-w-4xl">
              {activeCat.intro}
            </p>
            <div className="mt-8 md:mt-10 flex flex-wrap gap-2 md:gap-3">
              {activeCat.subCategories.map(sc => (
                <span key={sc} className="px-4 py-2 rounded-full text-xs md:text-sm font-heading font-medium border" style={{ borderColor: activeCat.accent + "40", color: activeCat.accent, background: activeCat.accent + "10" }}>
                  {sc}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product grid */}
      <section className="pb-20 md:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <motion.div
            key={activeCat.id + "-grid"}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {activeCat.products.map((p, i) => (
              <ProductCard key={p.id + i} product={p} accent={activeCat.accent} index={i} />
            ))}
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Products;
